<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useI18n } from 'vue-i18n';
import { fetchWooCommerceOrders } from '../../api/woocommerce';
import {
  buildConversationRoute,
  buildWooCommerceOrderDetailsRoute,
  getLatestConversation,
} from '../../helpers/navigation';
import { groupWooCommerceRecordsByStore } from '../../helpers/stores';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const loading = ref(true);
const error = ref('');
const orders = ref([]);

const accountId = computed(() => Number(route.params.accountId));
const contactId = computed(() => props.selectedContact?.id);
const hasSearchableInfo = computed(
  () => !!props.selectedContact?.email || !!props.selectedContact?.phoneNumber
);
const translatedError = computed(() =>
  error.value ? t('WOOCOMMERCE_PAGES.ORDERS.ERROR') : ''
);
const groupedOrders = computed(() =>
  groupWooCommerceRecordsByStore(orders.value)
);

const customerDetails = order =>
  [order.customer_email || '—', order.customer_phone || '—'].join(' · ');

const orderDetails = order =>
  [
    order.status || '—',
    order.payment_status || '—',
    [order.total || '—', order.currency || ''].filter(Boolean).join(' '),
  ].join(' · ');

const fetchOrders = async () => {
  if (!contactId.value || !hasSearchableInfo.value) {
    loading.value = false;
    orders.value = [];
    error.value = '';
    return;
  }

  try {
    loading.value = true;
    error.value = '';
    const response = await fetchWooCommerceOrders(
      accountId.value,
      contactId.value
    );
    orders.value = response.data.orders || [];
  } catch {
    error.value = 'WOOCOMMERCE_PAGES.ORDERS.ERROR';
  } finally {
    loading.value = false;
  }
};

const openOrder = order => {
  if (!order?.record_id) return;

  router.push(
    buildWooCommerceOrderDetailsRoute(accountId.value, order.record_id)
  );
};

const openExternalOrder = orderUrl => {
  if (!orderUrl) return;

  window.open(orderUrl, '_blank', 'noopener,noreferrer');
};

const openLatestConversation = async () => {
  if (!contactId.value) return;

  await store.dispatch('contactConversations/get', contactId.value);
  const conversationsGetter =
    store.getters['contactConversations/getContactConversation'];
  const conversations = conversationsGetter
    ? conversationsGetter(contactId.value)
    : [];
  const latestConversation = getLatestConversation(conversations);

  if (!latestConversation?.id) {
    useAlert(t('WOOCOMMERCE_PAGES.NO_CONVERSATION'));
    return;
  }

  router.push(buildConversationRoute(accountId.value, latestConversation.id));
};

watch(
  () => [contactId.value, hasSearchableInfo.value],
  () => {
    fetchOrders();
  }
);

onMounted(() => {
  fetchOrders();
});
</script>

<template>
  <div class="flex flex-col gap-4 px-6 py-4">
    <div class="flex flex-wrap gap-2">
      <Button
        :disabled="!contactId"
        variant="outline"
        color="slate"
        :label="$t('WOOCOMMERCE_PAGES.OPEN_LAST_CONVERSATION')"
        @click="openLatestConversation"
      />
    </div>

    <div
      v-if="!hasSearchableInfo"
      class="rounded-md border border-dashed border-n-weak p-4 text-sm text-n-slate-11"
    >
      {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.NO_ORDERS') }}
    </div>
    <div v-else-if="loading" class="flex items-center justify-center py-6">
      <Spinner />
    </div>
    <div
      v-else-if="error"
      class="rounded-md border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-12"
    >
      {{ translatedError }}
    </div>
    <div
      v-else-if="!orders.length"
      class="rounded-md border border-dashed border-n-weak p-4 text-sm text-n-slate-11"
    >
      {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.NO_ORDERS') }}
    </div>
    <div v-else class="grid gap-4">
      <div
        v-for="group in groupedOrders"
        :key="group.id"
        class="rounded-lg border border-n-weak bg-n-alpha-2 p-4"
      >
        <div class="mb-3 flex items-center justify-between gap-3">
          <div>
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{
                $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.STORE_GROUP', {
                  store: group.label,
                  count: group.records.length,
                })
              }}
            </p>
          </div>
        </div>

        <div class="grid gap-4">
          <div
            v-for="order in group.records"
            :key="`${group.id}-${order.id}`"
            class="rounded-md border border-n-weak bg-n-alpha-3 p-4"
          >
            <div
              class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
            >
              <div class="min-w-0 space-y-1">
                <p class="mb-0 text-base font-semibold text-n-slate-12">
                  {{
                    $t('WOOCOMMERCE_PAGES.ORDERS.ORDER_NUMBER', {
                      number: order.number || order.id,
                    })
                  }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{
                    order.customer_name ||
                    order.customer_email ||
                    order.customer_phone ||
                    '—'
                  }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ customerDetails(order) }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ orderDetails(order) }}
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                <Button
                  :disabled="!order.record_id"
                  :label="$t('WOOCOMMERCE_PAGES.OPEN_ORDER')"
                  @click="openOrder(order)"
                />
                <Button
                  v-if="order.admin_url"
                  variant="outline"
                  color="slate"
                  :label="$t('WOOCOMMERCE_PAGES.OPEN_IN_WOOCOMMERCE')"
                  @click="openExternalOrder(order.admin_url)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
